import { useId, useState, type ReactNode } from 'react';
import type { ZoneBox } from '../../types/api';
import {
  BRAND_ROLES,
  BRAND_ROLE_NAMES,
  DARK_INK,
  LIGHT_INK,
  clampBox,
  colorName,
  isHexColor,
  resolveColor,
  type BrandPalette,
  type ZoneMinimum,
} from './communityZones';

/**
 * The design builder's control language. Every inspector in the studio is
 * built from these and nothing else, so one choice looks chosen the same way
 * everywhere (ink fill, paper text; an ink ring on a colour swatch), every
 * control is 32px tall under a 12px label, and units sit inside the input.
 *
 * Each is a real form control underneath — radios, checkboxes, number inputs,
 * a colour input — so the inspector is keyboard-complete without any bespoke
 * key handling, and every one is labelled.
 */

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

export interface BuilderSectionProps {
  title: string;
  /** Open on first render; the author's own toggles are kept after that. */
  defaultOpen?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

/**
 * One collapsible group of the inspector: a `details` whose summary is the
 * keyboard route in, holding a `fieldset` so a locked template disables every
 * control inside at once.
 */
export function BuilderSection({
  title,
  defaultOpen = true,
  disabled = false,
  children,
}: BuilderSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <details
      className="builder-section"
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary className="builder-section__summary">
        <span className="builder-section__title">{title}</span>
        <Chevron />
      </summary>
      <fieldset className="builder-section__body" disabled={disabled}>
        <legend className="visually-hidden">{title}</legend>
        {children}
      </fieldset>
    </details>
  );
}

/** A quieter disclosure inside a section: the settings most designs leave alone. */
export function BuilderMore({ label, children }: { label: string; children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <details
      className="builder-more"
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary className="builder-more__summary">
        <Chevron />
        {label}
      </summary>
      <div className="builder-more__body">{children}</div>
    </details>
  );
}

function Chevron() {
  return (
    <svg
      className="builder-chevron"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** At most one short line under a section, only where the author cannot guess. */
export function BuilderHint({ children }: { children: ReactNode }) {
  return <p className="builder-hint">{children}</p>;
}

/** A two-column row of controls; the inspector never goes wider than two. */
export function BuilderGrid({ children }: { children: ReactNode }) {
  return <div className="builder-grid">{children}</div>;
}

/* ------------------------------------------------------------------ */
/* Labels and counters                                                 */
/* ------------------------------------------------------------------ */

/** Share of a limit at which a counter starts warning. */
const NEAR_LIMIT_RATIO = 0.9;

/** A `count/limit` readout that sits on a field's label row. */
export function BuilderCount({ count, limit }: { count: number; limit: number }) {
  const tone =
    count > limit
      ? ' builder-count--over'
      : count >= limit * NEAR_LIMIT_RATIO
        ? ' builder-count--near'
        : '';
  return (
    <span className={`builder-count${tone}`}>
      {count}/{limit}
      <span className="visually-hidden"> characters</span>
    </span>
  );
}

export interface TextFieldProps {
  label: string;
  /** Said on hover over the label: the detail that used to be a paragraph. */
  title?: string;
  value: string;
  maxLength?: number;
  /** Draws `count/limit` on the label row; the limit is advisory, not `maxLength`. */
  counterLimit?: number;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

/** A labelled text input, or a textarea when `rows` is given. */
export function TextField({
  label,
  title,
  value,
  maxLength,
  counterLimit,
  rows,
  placeholder,
  disabled = false,
  onChange,
}: TextFieldProps) {
  const id = useId();
  return (
    <div className="builder-field">
      <div className="builder-field__head">
        <label className="builder-label" htmlFor={id} title={title}>
          {label}
        </label>
        {counterLimit !== undefined ? (
          <BuilderCount count={value.length} limit={counterLimit} />
        ) : null}
      </div>
      {rows !== undefined ? (
        <textarea
          id={id}
          className="builder-input builder-input--area"
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className="builder-input"
          type="text"
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}

export interface SelectFieldProps<T extends string> {
  label: string;
  value: T;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  onChange: (value: T) => void;
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: SelectFieldProps<T>) {
  const id = useId();
  return (
    <div className="builder-field">
      <label className="builder-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="builder-input builder-input--select"
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Numbers                                                             */
/* ------------------------------------------------------------------ */

export interface NumberFieldProps {
  label: string;
  /** Said on hover over the label. */
  title?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  /** Drawn inside the input, at its right edge: `px`, `%`. */
  unit?: string;
  disabled?: boolean;
  onCommit: (value: number) => void;
}

/**
 * A number the author can type through: an in-range value commits on every
 * keystroke, a value on its way somewhere (typing 1 on the way to 14) is held
 * locally, and leaving the field commits the nearest legal number.
 */
export function NumberField({
  label,
  title,
  value,
  min,
  max,
  step = 1,
  unit,
  disabled = false,
  onCommit,
}: NumberFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const id = useId();

  function commit(raw: string) {
    const parsed = Number(raw);
    setDraft(null);
    if (raw.trim() === '' || !Number.isFinite(parsed)) return;
    onCommit(Math.min(max, Math.max(min, parsed)));
  }

  return (
    <div className="builder-field">
      <label className="builder-label" htmlFor={id} title={title}>
        {label}
      </label>
      <span className={unit ? 'builder-adorned builder-adorned--unit' : 'builder-adorned'}>
        <input
          id={id}
          className="builder-input builder-input--number"
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={draft ?? String(value)}
          disabled={disabled}
          onChange={(event) => {
            const raw = event.target.value;
            setDraft(raw);
            const parsed = Number(raw);
            if (raw.trim() !== '' && Number.isFinite(parsed) && parsed >= min && parsed <= max) {
              onCommit(parsed);
            }
          }}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(event.currentTarget.value);
            }
          }}
        />
        {unit ? (
          <span className="builder-adorned__unit" aria-hidden="true">
            {unit}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export interface RangeFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** The value as read out on the label row: `90%`, `6px`. */
  readout: string;
  onChange: (value: number) => void;
}

/** A slider with its label and readout on one row above it. */
export function RangeField({ label, value, min, max, step, readout, onChange }: RangeFieldProps) {
  const id = useId();
  return (
    <div className="builder-field">
      <div className="builder-field__head">
        <label className="builder-label" htmlFor={id}>
          {label}
        </label>
        <output className="builder-readout" htmlFor={id}>
          {readout}
        </output>
      </div>
      <input
        id={id}
        className="builder-range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={readout}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Choices                                                             */
/* ------------------------------------------------------------------ */

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Tooltip, and the spoken name when the label is an icon. */
  title?: string;
  disabled?: boolean;
}

export interface SegmentedProps<T extends string> {
  label: string;
  /** Drawn after the label in muted text: the chosen value said in words. */
  note?: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  disabled?: boolean;
  onChange: (value: T) => void;
}

/** A row of radios drawn as one control, a full row wide, never wrapping. */
export function Segmented<T extends string>({
  label,
  note,
  value,
  options,
  disabled = false,
  onChange,
}: SegmentedProps<T>) {
  const name = useId();
  return (
    <fieldset className="builder-segmented" disabled={disabled}>
      <legend className="builder-label">
        {label}
        {note ? <span className="builder-label__note"> · {note}</span> : null}
      </legend>
      <div className="builder-segmented__options">
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <label
              className={
                isSelected
                  ? 'builder-segmented__option builder-segmented__option--selected'
                  : 'builder-segmented__option'
              }
              key={option.value}
              title={option.title}
            >
              <input
                className="visually-hidden"
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                disabled={option.disabled}
                aria-label={typeof option.label === 'string' ? undefined : option.title}
                onChange={() => onChange(option.value)}
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface SwitchProps {
  label: string;
  /** Keeps the label for screen readers only, beside a row that names it. */
  hideLabel?: boolean;
  title?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

/** An on/off switch: a real checkbox with `role="switch"`, drawn as a track. */
export function Switch({
  label,
  hideLabel = false,
  title,
  checked,
  disabled = false,
  onChange,
}: SwitchProps) {
  return (
    <label className="builder-switch" title={title}>
      <input
        className="builder-switch__input"
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="builder-switch__track" aria-hidden="true" />
      <span className={hideLabel ? 'visually-hidden' : 'builder-switch__label'}>{label}</span>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Colour                                                              */
/* ------------------------------------------------------------------ */

const TEXT_KEYWORDS = [
  {
    value: 'auto',
    title: 'Auto: light or dark, picked from what is behind the box',
    swatch: null,
  },
  { value: 'light', title: 'Light', swatch: LIGHT_INK },
  { value: 'dark', title: 'Dark', swatch: DARK_INK },
] as const;

/** Tooltip on a brand swatch: whose colour it really is. */
const BRAND_TITLE = "the member's own brand colour when they post";

export interface ColorFieldProps {
  label: string;
  value: string;
  palette: BrandPalette;
  /** Offers Auto / Light / Dark beside the colours: a text box's ink. */
  allowTextKeywords?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
}

/**
 * A colour, as a brand role or a fixed hex: one row of swatches, the chosen
 * one ringed in ink and named beside the label. The brand roles are drawn in
 * the author's own palette; whoever posts on the design sees theirs.
 */
export function ColorField({
  label,
  value,
  palette,
  allowTextKeywords = false,
  disabled = false,
  onChange,
}: ColorFieldProps) {
  const name = useId();
  const isCustom = isHexColor(value);
  const [hexDraft, setHexDraft] = useState<string | null>(null);
  const customHex = isCustom ? value.toUpperCase() : resolveColor(value, palette).toUpperCase();
  const chosen = isCustom ? `Custom ${value.toUpperCase()}` : colorName(value);

  function swatch(ref: string, title: string, fill: string | null, extraClass = '') {
    const isSelected = value === ref;
    return (
      <label
        className={[
          'builder-swatch',
          isSelected ? 'builder-swatch--selected' : '',
          fill === null ? 'builder-swatch--auto' : '',
          extraClass,
        ]
          .filter(Boolean)
          .join(' ')}
        key={ref}
        title={title}
      >
        <input
          className="visually-hidden"
          type="radio"
          name={name}
          value={ref}
          checked={isSelected}
          aria-label={title}
          onChange={() => onChange(ref)}
        />
        <span
          className="builder-swatch__fill"
          style={fill === null ? undefined : { background: fill }}
          aria-hidden="true"
        />
      </label>
    );
  }

  return (
    <fieldset className="builder-color" disabled={disabled}>
      <legend className="builder-label">
        {label}
        <span className="builder-label__note"> · {chosen}</span>
      </legend>
      <div className="builder-color__row">
        {allowTextKeywords ? (
          <>
            {TEXT_KEYWORDS.map((keyword) => swatch(keyword.value, keyword.title, keyword.swatch))}
            <span className="builder-color__divider" aria-hidden="true" />
          </>
        ) : null}
        {BRAND_ROLES.map((role) =>
          swatch(
            role,
            `Brand ${BRAND_ROLE_NAMES[role].toLowerCase()}: ${BRAND_TITLE}`,
            resolveColor(role, palette),
          ),
        )}
        <span className="builder-color__divider" aria-hidden="true" />
        <label
          className={
            isCustom
              ? 'builder-swatch builder-swatch--custom builder-swatch--selected'
              : 'builder-swatch builder-swatch--custom'
          }
          title="Custom: a fixed colour, drawn exactly as you pick it"
        >
          <input
            className="visually-hidden"
            type="radio"
            name={name}
            value="custom"
            checked={isCustom}
            aria-label="Custom colour"
            onChange={() => onChange(customHex)}
          />
          <span
            className="builder-swatch__fill"
            style={isCustom ? { background: customHex } : undefined}
            aria-hidden="true"
          />
        </label>
      </div>
      {isCustom ? (
        <div className="builder-color__custom">
          <input
            className="builder-color__picker"
            type="color"
            aria-label={`${label}, custom colour`}
            value={value.toLowerCase()}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
          />
          <input
            className="builder-input builder-color__hex"
            type="text"
            aria-label={`${label}, hex value`}
            maxLength={7}
            spellCheck={false}
            value={hexDraft ?? value.toUpperCase()}
            onChange={(event) => {
              const raw = event.target.value.trim();
              const next = raw.startsWith('#') ? raw : `#${raw}`;
              setHexDraft(event.target.value);
              if (isHexColor(next)) onChange(next.toUpperCase());
            }}
            onBlur={() => setHexDraft(null)}
          />
        </div>
      ) : null}
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* Box                                                                 */
/* ------------------------------------------------------------------ */

export interface BoxFieldsProps {
  box: ZoneBox;
  minimum: ZoneMinimum;
  disabled?: boolean;
  onChange: (box: ZoneBox) => void;
}

function toPercent(fraction: number): number {
  return Math.round(fraction * 1000) / 10;
}

/** A box's place and size typed in percent, 2x2 — the precise route beside the drag. */
export function BoxFields({ box, minimum, disabled = false, onChange }: BoxFieldsProps) {
  function commit(changes: Partial<ZoneBox>) {
    onChange(clampBox({ ...box, ...changes }, minimum));
  }
  return (
    <div className="builder-grid">
      <NumberField
        label="X"
        title="From the left edge"
        unit="%"
        min={0}
        max={100}
        step={0.5}
        value={toPercent(box.x)}
        disabled={disabled}
        onCommit={(value) => commit({ x: value / 100 })}
      />
      <NumberField
        label="Y"
        title="From the top edge"
        unit="%"
        min={0}
        max={100}
        step={0.5}
        value={toPercent(box.y)}
        disabled={disabled}
        onCommit={(value) => commit({ y: value / 100 })}
      />
      <NumberField
        label="Width"
        unit="%"
        min={toPercent(minimum.w)}
        max={100}
        step={0.5}
        value={toPercent(box.w)}
        disabled={disabled}
        onCommit={(value) => commit({ w: value / 100 })}
      />
      <NumberField
        label="Height"
        unit="%"
        min={toPercent(minimum.h)}
        max={100}
        step={0.5}
        value={toPercent(box.h)}
        disabled={disabled}
        onCommit={(value) => commit({ h: value / 100 })}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

export type BuilderIconName =
  | 'up'
  | 'down'
  | 'duplicate'
  | 'delete'
  | 'align-left'
  | 'align-center'
  | 'align-right'
  | 'valign-top'
  | 'valign-middle'
  | 'valign-bottom'
  | 'text'
  | 'logo'
  | 'photo'
  | 'value'
  | 'plus'
  | 'swap';

const ICON_PATHS: Record<BuilderIconName, ReactNode> = {
  up: <path d="M8 13V3M4 7l4-4 4 4" />,
  down: <path d="M8 3v10M4 9l4 4 4-4" />,
  duplicate: (
    <>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 3.5v-1h-8v8h1" />
    </>
  ),
  delete: <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 9h5.6l.7-9M7 7v4.5M9 7v4.5" />,
  'align-left': <path d="M2.5 4h11M2.5 8h7M2.5 12h9" />,
  'align-center': <path d="M2.5 4h11M4.5 8h7M3.5 12h9" />,
  'align-right': <path d="M2.5 4h11M6.5 8h7M4.5 12h9" />,
  'valign-top': <path d="M2.5 2.5h11M5 6h6M6 9h4" />,
  'valign-middle': <path d="M5 4.5h6M2.5 8h11M5 11.5h6" />,
  'valign-bottom': <path d="M6 7h4M5 10h6M2.5 13.5h11" />,
  text: <path d="M3.5 4V3h9v1M8 3v10M6 13h4" />,
  logo: (
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
      <path d="M5.5 10.5 8 5.5l2.5 5" />
    </>
  ),
  photo: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <circle cx="8" cy="6.8" r="1.8" />
      <path d="M4.6 12.2c.8-1.6 2-2.3 3.4-2.3s2.6.7 3.4 2.3" />
    </>
  ),
  value: (
    <>
      <rect x="2.5" y="4" width="11" height="8" rx="1.5" />
      <path d="M6 10V6.2L5 7M8.5 6.5h2l-1.6 3.5" />
    </>
  ),
  plus: <path d="M8 3.5v9M3.5 8h9" />,
  swap: <path d="M4 5.5h8.5L10 3M12 10.5H3.5L6 13" />,
};

/** A 16px line icon in the current colour; always decorative, named by its control. */
export function BuilderIcon({ name }: { name: BuilderIconName }) {
  return (
    <svg
      className="builder-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

export interface IconButtonProps {
  icon: BuilderIconName;
  label: string;
  tone?: 'danger';
  disabled?: boolean;
  /** Said on hover when it differs from the label: why it is disabled. */
  title?: string;
  onClick: () => void;
}

/** A square icon action; its label is both the tooltip and the accessible name. */
export function IconButton({
  icon,
  label,
  tone,
  disabled = false,
  title,
  onClick,
}: IconButtonProps) {
  return (
    <button
      className={
        tone === 'danger'
          ? 'builder-icon-button builder-icon-button--danger'
          : 'builder-icon-button'
      }
      type="button"
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
    >
      <BuilderIcon name={icon} />
    </button>
  );
}
